import React, { useState } from "react";
import { X, AlertTriangle, Bug, Shield, Package, Landmark, MessageSquare, Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const CATEGORIES = [
  { k: "app_bug",        label: "App bug or glitch",      Icon: Bug,           color: "#1e88e5" },
  { k: "cheating",       label: "Someone is cheating",    Icon: AlertTriangle, color: "#E53935" },
  { k: "tos_violation",  label: "Terms of service",       Icon: Shield,        color: "#D7A62A" },
  { k: "drop_issue",     label: "Problem with a drop",    Icon: Package,       color: "#76B82A" },
  { k: "waymark_issue",  label: "Problem with a waymark", Icon: Landmark,      color: "#76B82A" },
  { k: "other",          label: "Something else",         Icon: MessageSquare, color: "#F5F2E8" },
];

export default function ReportIssueModal({ open, onClose }) {
  const { user } = useAuth();
  const [category, setCategory] = useState("app_bug");
  const [subject, setSubject] = useState("");
  const [details, setDetails] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !details.trim()) {
      toast.error("Give us a subject and a few details.");
      return;
    }
    if (!user && !email.trim()) {
      toast.error("Please include an email so we can reach you.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/support/report", {
        category,
        subject: subject.trim(),
        details: details.trim(),
        contact_email: user ? undefined : email.trim(),
        page_url: typeof window !== "undefined" ? window.location.href : undefined,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't submit right now. Try again in a minute.");
    } finally {
      setSubmitting(false);
    }
  };

  const closeAndReset = () => {
    setDone(false);
    setCategory("app_bug");
    setSubject("");
    setDetails("");
    setEmail("");
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)" }}
      onClick={closeAndReset}
      data-testid="report-modal"
    >
      <div
        className="loot-card-flat p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={closeAndReset}
          className="absolute top-4 right-4 text-[#F5F2E8]/50 hover:text-[#F5F2E8]"
          aria-label="Close"
          data-testid="report-close"
        >
          <X size={18} />
        </button>

        {done ? (
          <div className="py-6 text-center" data-testid="report-success">
            <CheckCircle2 size={40} className="mx-auto mb-3" style={{ color: "#76B82A" }} />
            <div className="font-display uppercase text-3xl tracking-tight">
              Thanks, got it.
            </div>
            <p className="text-sm text-[#F5F2E8]/70 mt-3">
              We read every report. If it's urgent, we'll reach out at{" "}
              <span className="font-mono text-[#76B82A]">{user?.email || email}</span>.
            </p>
            <button
              type="button"
              onClick={closeAndReset}
              className="btn-loot mt-5"
              data-testid="report-done-btn"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <div className="overline">Report an issue</div>
              <h2 className="font-display uppercase text-3xl tracking-tight mt-1">
                Tell us <span style={{ color: "#E53935" }}>what's up</span>
              </h2>
              <p className="text-xs font-mono text-[#F5F2E8]/55 mt-1">
                Bug, cheater, ToS violation, or just a question. We read every one.
              </p>
            </div>

            <div>
              <label className="overline block mb-2">What's the issue?</label>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORIES.map(({ k, label, Icon, color }) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setCategory(k)}
                    className="flex items-center gap-2 p-2 border text-xs text-left transition"
                    style={{
                      borderColor: category === k ? color : "rgba(245,242,232,0.15)",
                      background: category === k ? `${color}15` : undefined,
                      color: category === k ? color : "#F5F2E8",
                    }}
                    data-testid={`report-cat-${k}`}
                  >
                    <Icon size={14} className="shrink-0" />
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            </div>

            <input
              className="loot-input"
              placeholder="Subject (one line)"
              value={subject}
              maxLength={160}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="report-subject"
            />
            <textarea
              className="loot-input"
              rows={5}
              maxLength={4000}
              placeholder={
                category === "cheating"
                  ? "Who, what, when, where. Usernames or photos help a lot."
                  : category === "tos_violation"
                  ? "Describe the violation and link to the profile / drop / message if possible."
                  : category === "app_bug"
                  ? "What you did, what you expected, what actually happened. iPhone or Android?"
                  : "Tell us what happened in as much detail as you can."
              }
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              data-testid="report-details"
            />

            {!user && (
              <input
                className="loot-input"
                type="email"
                placeholder="Your email (so we can follow up)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="report-email"
              />
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn-loot w-full inline-flex items-center justify-center gap-2"
              data-testid="report-submit"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
              {submitting ? "Sending…" : "Send report"}
            </button>
            <p className="text-[10px] font-mono text-[#F5F2E8]/45 uppercase tracking-widest text-center">
              Anonymous reports respected · we never publish reporter identities
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
